/**
 * Print paper size handling.
 *
 * Background: the print stylesheets used to carry a rule shaped like
 *
 *   body.printing-recipe[data-print-paper="letter"] @page { size: letter; }
 *
 * which is invalid CSS — `@page` is a page-context at-rule and cannot be
 * nested inside a selector. Browsers dropped it silently, so choosing Letter
 * always printed A4. (The PDF *download* path was never affected; it passes
 * the paper size straight to jsPDF.)
 *
 * There is no way to express "page size depends on a body attribute" in
 * static CSS, so the rule is injected at print time instead. `data-print-paper`
 * is still set on <body> because the stylesheets use it for other, valid
 * selectors — this module only owns the @page descriptor.
 */

const STYLE_ELEMENT_ID = 'vh-print-paper-rule'

const PAPER_RULES = {
  letter: { size: 'letter portrait', margin: '0.6in' },
  a4: { size: 'A4 portrait', margin: '15mm' },
}

/**
 * Map a UI language to a paper size.
 * en (en-US, en-GB, en) -> Letter; everything else (de, pt-BR, ...) -> A4.
 */
export function getPaperSize(language) {
  const lang = (language || 'en').toLowerCase()
  return lang.startsWith('en') ? 'letter' : 'a4'
}

/**
 * Inject (or replace) the @page rule for the chosen paper size.
 *
 * Appended to <head> so it comes after the imported print stylesheets and
 * wins the cascade over their static `@page { size: A4 portrait; }` default.
 */
export function applyPrintPaper(paper) {
  const rule = PAPER_RULES[paper] || PAPER_RULES.a4

  let style = document.getElementById(STYLE_ELEMENT_ID)
  if (!style) {
    style = document.createElement('style')
    style.id = STYLE_ELEMENT_ID
    document.head.appendChild(style)
  }
  style.textContent = `@page { size: ${rule.size}; margin: ${rule.margin}; }`
  return style
}

/** Remove the injected @page rule, restoring the stylesheet default. */
export function clearPrintPaper() {
  const style = document.getElementById(STYLE_ELEMENT_ID)
  if (style && style.parentNode) {
    style.parentNode.removeChild(style)
  }
}
